import { useEffect, useReducer, useRef, useState, createContext, useMemo } from 'react';
import produce from 'immer';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/navigation';

// types
import type { WebShopLoginData } from 'utils/tracking/types';

// utils
import {
  webshopEnabledSelector,
  campusDashboardUrlSelector,
  campusReferenceUrlSelector,
} from 'utils/selectors/globalsSelectors';
import { webshopMaintenanceEnabledSelector } from 'utils/selectors/maintenanceSelectors';
import { addFetchInterceptor } from 'utils/interceptors';
import { mergeCart } from 'components/ShoppingBasket/actions';
import { CookieStorage } from './CookieStorage';
import { addSeconds } from 'date-fns';
import { useGetEndpoint } from 'utils/hooks/use-endpoint';
import { useCurrentLanguage } from 'utils/hooks/useCurrentLanguage';
import { homepageSelector } from 'utils/selectors/homepageListSelectors';
import { useTracking } from 'utils/tracking/track';
import { useDebouncedCallback } from 'use-debounce';

export const SET_SESSION = 'SET_SESSION';
export const SET_SESSION_LOADING = 'SET_SESSION_LOADING';
export const SET_CAMPUS_COURSE_TOKEN = 'SET_CAMPUS_COURSE_TOKEN';
const SET_CAMPUS_TOKEN = 'SET_CAMPUS_TOKEN';
const SET_CIAM = 'SET_CIAM';
const SET_CAMPUS_TOKEN_LOGOUT = 'SET_CAMPUS_TOKEN_LOGOUT';

export interface State {
  session: string | null;
  sessionIsLoading: boolean;
  ciam: {
    UID: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    salutation: string;
    country: string;
    city: string;
    zip: string;
  } | null;
  campus: {
    token: string | null;
    refreshToken: string | null;
    loading: boolean;
    courses: {
      [key: string]: {
        token: string;
        refreshToken: string;
      };
    };
  };
  gigyaWindow?: any;
}

const initialState = {
  session: null,
  sessionIsLoading: false,
  ciam: null,
  campus: {
    token: null,
    refreshToken: null,
    loading: true,
    courses: {},
  },
};

export const SessionContext = createContext<{
  state: State;
  dispatch: React.Dispatch<any>;
  redirectUrl: { current?: string };
}>({ state: initialState, dispatch: () => null, redirectUrl: {} });

const sessionReducer = produce((draft, action) => {
  switch (action.type) {
    case SET_SESSION:
      draft.session = action.session;
      break;

    case SET_SESSION_LOADING:
      draft.sessionIsLoading = action.isLoading;
      break;

    case SET_CIAM:
      draft.ciam = action.ciam;
      break;

    case SET_CAMPUS_COURSE_TOKEN:
      if (draft.campus.courses[action.courseId]) {
        draft.campus.courses[action.courseId] = {
          ...draft.campus.courses[action.courseId],
          ...action.data,
        };
      } else {
        draft.campus.courses[action.courseId] = action.data;
      }

      break;

    case SET_CAMPUS_TOKEN:
      draft.campus.token = action.token;
      if (action.refreshToken) {
        draft.campus.refreshToken = action.refreshToken;
      }
      if (typeof action.loading !== 'undefined') {
        draft.campus.loading = action.loading;
      }
      break;

    case SET_CAMPUS_TOKEN_LOGOUT:
      draft.campus.token = null;
      draft.campus.refreshToken = null;
      draft.campus.courses = {};
      break;

    default:
  }

  draft.campus.token = CookieStorage.getItem('campus_access_token');
  draft.campus.refreshToken = CookieStorage.getItem('campus_refresh_token');
}, initialState);

/**
 * Add Ciam header to webshop requests
 */
let headersTimeout: ReturnType<typeof setTimeout> | null = null;

export async function addCiamHeader(gigyaWindow) {
  if (!headersTimeout && gigyaWindow) {
    addFetchInterceptor('ciam-signature', async (config) => {
      if (config.url.includes('/webshop/')) {
        return new Promise((resolve) => {
          const setHeader = ({ UIDSignature, signatureTimestamp }) => {
            config.init.headers = {
              ...(config.init.headers || {}),
              UIDSignature,
              signatureTimestamp,
            };
            resolve(config);
          };
          gigyaWindow.socialize.getUserInfo({
            format: 'jsonp',
            callback: setHeader,
            signIDs: true,
          });
        });
      }
      return config;
    });

    headersTimeout = setTimeout(() => {
      addCiamHeader(gigyaWindow);
    }, 2000);
  }
}

const setStorageToken = (gigyaWindow) =>
  new Promise<void>((resolve) => {
    gigyaWindow.accounts.getJWT({
      callback: async ({ id_token: idToken, requestParams: { sessionExpiration }, ...rest }) => {
        await addCiamHeader(gigyaWindow);
        CookieStorage.setItem('ciam_jwt', idToken, addSeconds(new Date(), sessionExpiration));
        window.localStorage.setItem('_ciamtoken', idToken);
        resolve();
      },
    });
  });

export const initSamlWorkflow = (href: string, lang: string) => {
  let redirectURL = href;

  if (!href.startsWith('http')) {
    const divider = href.startsWith('/') ? '' : '/';
    redirectURL = `${window.location.origin}${divider}${href}`;
  }

  setTimeout(() => {
    window.location.href = `/api/campus/saml/login/?l=${lang}&mode=initSSO&spName=campus&redirectURL=${encodeURIComponent(
      redirectURL,
    )}`;
  }, 3000);
};

const removeCiamLocalStorage = () => {
  window.localStorage.removeItem('_ciamtoken');
  CookieStorage.removeItem('ciam_jwt');
};

export function SessionProvider({ children }: React.PropsWithChildren) {
  const [state, dispatch] = useReducer(sessionReducer, initialState);
  const redirectUrl = useRef<string | undefined>(undefined);
  const value = useMemo(() => ({ state, dispatch, redirectUrl }), [state, dispatch]);
  const webshopEnabled = useSelector(webshopEnabledSelector);
  const webshopDowntime = useSelector(webshopMaintenanceEnabledSelector);
  const campusDashboardUrl = useSelector(campusDashboardUrlSelector);
  const campusReferenceUrl = useSelector(campusReferenceUrlSelector);
  const lang = useCurrentLanguage();
  const { getEndpoint } = useGetEndpoint();
  const homepage = useSelector(homepageSelector);
  const router = useRouter();
  const [gigyaWindow, setGigyaWindow] = useState<Window | undefined>(undefined);
  const { trackWebshop } = useTracking();
  const debouncedTrackWebshopLogin = useDebouncedCallback<
    (event: 'login', data: WebShopLoginData) => void
  >(trackWebshop, 1000);

  useEffect(() => {
    const storageToken = window.localStorage.getItem('gigyaAccessToken');
    const storageRefresh = window.localStorage.getItem('gigyaRefreshToken');
    dispatch({
      type: SET_CAMPUS_TOKEN,
      token: storageToken,
      refreshToken: storageRefresh,
      loading: false,
    });
    window.addEventListener('gigya.session.ready', () => {
      setGigyaWindow(window);
    });

    // Check if we need to run SAML workflow after reload
    const needsSamlWorkflow = window.localStorage.getItem('needsSamlWorkflow');
    if (needsSamlWorkflow === 'true') {
      const samlRedirectUrl = window.localStorage.getItem('samlRedirectUrl') || campusDashboardUrl;
      window.localStorage.removeItem('needsSamlWorkflow');
      window.localStorage.removeItem('samlRedirectUrl');
      initSamlWorkflow(samlRedirectUrl, lang);
    }
  }, [campusDashboardUrl, lang]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window?.gigya) {
      window.gigya.accounts.addEventHandlers({
        onLogin: async (props) => {
          debouncedTrackWebshopLogin('login', {
            method: 'Geberit ID',
          });
          const { profile: user, data, UID } = props;
          const { relationship, salutation } = data || {};
          await addCiamHeader(window.gigya);
          dispatch({
            type: SET_CIAM,
            ciam: { ...user, role: relationship, salutation, UID },
          });
          dispatch({ type: SET_SESSION_LOADING, isLoading: false });

          await setStorageToken(window.gigya);

          // hide login screen for geberit employess after login
          window.gigya.accounts.hideScreenSet({
            screenSet: 'Geberit-RegistrationLogin',
            startScreen: 'geberit-login-nosc',
          });

          const session = window.localStorage.getItem('gsessid');

          // only campus pages can handle accessToken and refreshToken callbacks
          // so we only need to init the saml workflow if the user starts the login from
          // a campus site
          if (campusDashboardUrl.startsWith(window.location.pathname)) {
            // Store the information needed after reload
            window.localStorage.setItem('needsSamlWorkflow', 'true');
            window.localStorage.setItem(
              'samlRedirectUrl',
              redirectUrl.current || campusDashboardUrl,
            );
            redirectUrl.current = undefined;

            // reload page to ensure saml workflow is working all the time
            window.location.reload();
          }

          if (props.context.country && session && webshopEnabled && !webshopDowntime) {
            await addCiamHeader(window.gigya);
            await mergeCart(getEndpoint, session, UID, props.context.country);
          }
        },
        onLogout: async () => {
          removeCiamLocalStorage();
          await addCiamHeader(null);
          dispatch({ type: SET_CIAM, ciam: null });
          dispatch({ type: SET_SESSION_LOADING, isLoading: false });
          dispatch({ type: SET_CAMPUS_TOKEN_LOGOUT });

          if (CookieStorage.getItem('campus_refresh_token')) {
            const redirectURL = window.location.pathname.startsWith(campusReferenceUrl)
              ? campusReferenceUrl
              : window.location.pathname;
            const absoluteRedirectURL = `${window.location.origin}${redirectURL}`;

            window.location.href = `/api/campus/saml/logout?redirectURL=${encodeURIComponent(
              absoluteRedirectURL,
            )}`;
          } else if (homepage) {
            router.push(homepage.url);
          }
        },
      });

      const tokenJWT = window.localStorage.getItem('_ciamtoken');

      if (tokenJWT !== null) {
        addCiamHeader(window.gigya);

        dispatch({ type: SET_SESSION_LOADING, isLoading: true });
        window.gigya.accounts.getAccountInfo({
          callback: async ({ profile: user, data, UID, status }) => {
            if (status === 'FAIL') {
              await addCiamHeader(null);
              dispatch({ type: SET_CIAM, ciam: null });
              removeCiamLocalStorage();
              dispatch({ type: SET_SESSION_LOADING, isLoading: false });
            } else {
              const { relationship, salutation } = data || {};
              await addCiamHeader(window.gigya);
              dispatch({
                type: SET_CIAM,
                ciam: { ...user, role: relationship, salutation, UID },
              });

              setTimeout(() => {
                dispatch({ type: SET_SESSION_LOADING, isLoading: false });
              }, 200);
            }
          },
        });
      } else {
        dispatch({ type: SET_SESSION_LOADING, isLoading: false });
      }
    }

    return () => {
      addCiamHeader(null);
    };
  }, [gigyaWindow, router]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
